import { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { inr } from "@/lib/utils";
import PageHero from "@/components/PageHero";
import MetricCard from "@/components/MetricCard";
import WeeklyPayroll from "@/components/WeeklyPayroll";
import StaffLedger from "@/components/StaffLedger";
import LifetimeEarnings from "@/components/LifetimeEarnings";
import OfficeExpenses from "@/pages/OfficeExpenses";
import {
  Wallet, ArrowDownRight, AlertCircle, ReceiptText, IndianRupee, TrendingUp,
} from "lucide-react";

/**
 * iter61 — Financials & Payroll workspace (Admin-only).
 *
 * All money-side tooling lives here — decoupled from the operational
 * `/team` workspace so Coordinators never brush against it (RBAC enforced
 * at both route + sidebar layer). Composition:
 *
 *   ┌ Page Hero ────────────────────────────────────────────────┐
 *   │  Financials & Payroll · This month at a glance             │
 *   └────────────────────────────────────────────────────────────┘
 *   ┌ KPI Strip (4 cards) ───────────────────────────────────────┐
 *   │  Payroll · Office Spend · Net Outflow · Pending Shoot Dues │
 *   └────────────────────────────────────────────────────────────┘
 *   ┌ Tabs ──────────────────────────────────────────────────────┐
 *   │  Weekly Payroll │ Staff Ledger │ Lifetime │ Office Cash Book│
 *   └────────────────────────────────────────────────────────────┘
 *
 * KPI data sources:
 *   • GET /api/dashboard/monthly-financials?month=YYYY-MM  → payroll +
 *     office spend + net outflow for the current month.
 *   • GET /api/team/staff/earnings-summary → totals.outstanding_due
 *     (lifetime pending shoot dues across all staff).
 */

// iter206 — `ownerOnly` tabs are hidden (and their APIs 403) for non-Owner admins.
const TABS = [
  { key: "payroll",  label: "Weekly Payroll",   icon: Wallet, ownerOnly: true },
  { key: "ledger",   label: "Staff Ledger",     icon: IndianRupee },
  { key: "lifetime", label: "Lifetime Earnings", icon: TrendingUp, ownerOnly: true },
  { key: "expenses", label: "Office Cash Book", icon: ReceiptText },
];

function currentMonthKey() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function monthLabel(ym) {
  if (!ym) return "";
  const [y, m] = ym.split("-").map((v) => parseInt(v, 10));
  if (!y || !m) return ym;
  return new Date(y, m - 1, 1).toLocaleDateString("en-IN", { month: "long", year: "numeric" });
}

export default function Financials() {
  const { user } = useAuth();
  const isOwner = !!user?.is_owner;
  const tabs = TABS.filter((t) => isOwner || !t.ownerOnly);
  const [tab, setTab] = useState(isOwner ? "payroll" : "ledger");
  const monthKey = useMemo(currentMonthKey, []);

  const [kpi, setKpi] = useState({
    payroll: null, office: null, outflow: null, pendingDues: null, loading: true,
  });

  const loadKpis = useCallback(async () => {
    setKpi((s) => ({ ...s, loading: true }));
    // iter61 — `allSettled` so a single failing endpoint doesn't wipe out
    // every KPI card. Payroll/office/outflow live on /dashboard/monthly-
    // financials and pending-dues on /team/staff/earnings-summary — they
    // are independent surfaces, so failure of one shouldn't blank the other.
    const [finRes, earnRes] = await Promise.allSettled([
      api.get(`/dashboard/monthly-financials?month=${monthKey}`),
      api.get(`/team/staff/earnings-summary`),
    ]);
    const fin = finRes.status === "fulfilled" ? finRes.value.data : null;
    const earn = earnRes.status === "fulfilled" ? earnRes.value.data : null;
    setKpi({
      payroll:     fin?.outflow?.payroll?.total ?? 0,
      office:      fin?.outflow?.office?.total  ?? 0,
      outflow:     fin?.outflow?.total          ?? 0,
      pendingDues: earn?.totals?.outstanding_due ?? 0,
      loading: false,
    });
  }, [monthKey]);
  useEffect(() => { if (isOwner) loadKpis(); }, [loadKpis, isOwner]);

  return (
    <div className="space-y-6" data-testid="financials-page">
      <PageHero
        eyebrow={isOwner ? "Kahani Studio · Owner" : "Kahani Studio · Admin"}
        title="Financials & Payroll"
        subtitle={isOwner ? `Payroll, staff ledger, lifetime earnings and office cash book — snapshot for ${monthLabel(monthKey)}.` : "Staff ledger and office cash book."}
      />

      {/* -------- KPI Strip (this month) — Owner only ---------------- */}
      {isOwner && (
      <section
        className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4"
        data-testid="financials-kpi-strip"
      >
        <MetricCard
          eyebrow="Payroll · This Month"
          value={kpi.loading ? "—" : inr(kpi.payroll || 0)}
          subtitle="Weekly salary paid out"
          icon={Wallet}
          tone="wine"
          meta={monthLabel(monthKey)}
          loading={kpi.loading}
          testid="financials-kpi-payroll"
        />
        <MetricCard
          eyebrow="Office Spend · This Month"
          value={kpi.loading ? "—" : inr(kpi.office || 0)}
          subtitle="Petty-cash debits from the cash book"
          icon={ReceiptText}
          tone="amber"
          meta={monthLabel(monthKey)}
          loading={kpi.loading}
          testid="financials-kpi-office"
        />
        <MetricCard
          eyebrow="Net Outflow · This Month"
          value={kpi.loading ? "—" : inr(kpi.outflow || 0)}
          subtitle="Payroll + office expenses combined"
          icon={ArrowDownRight}
          tone="red"
          meta={monthLabel(monthKey)}
          loading={kpi.loading}
          testid="financials-kpi-outflow"
        />
        <MetricCard
          eyebrow="Pending Shoot Dues"
          value={kpi.loading ? "—" : inr(kpi.pendingDues || 0)}
          subtitle="Lifetime unpaid balance across all crew"
          icon={AlertCircle}
          tone={kpi.pendingDues > 0 ? "red" : "emerald"}
          meta={kpi.pendingDues > 0 ? "Action required" : "All settled"}
          loading={kpi.loading}
          testid="financials-kpi-pending-dues"
        />
      </section>
      )}

      {/* -------- Tabs ------------------------------------------------ */}
      <div className="flex flex-wrap gap-1 border-b border-stone-200 -mb-px" data-testid="financials-tabs">
        {tabs.map((t) => {
          const active = tab === t.key;
          const Icon = t.icon;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold border-b-2 -mb-px transition-colors ${
                active ? "text-rose-700 border-rose-700" : "text-stone-500 border-transparent hover:text-stone-800"
              }`}
              data-testid={`financials-tab-${t.key}`}
            >
              <Icon className="w-3.5 h-3.5" strokeWidth={1.8} /> {t.label}
            </button>
          );
        })}
      </div>

      {isOwner && tab === "payroll"  && <WeeklyPayroll />}
      {tab === "ledger"   && <StaffLedger />}
      {isOwner && tab === "lifetime" && <LifetimeEarnings />}
      {tab === "expenses" && <OfficeExpenses />}
    </div>
  );
}
